import { IVec3, Vec3 } from "@base/math/Vec3";
import { ImmHelper } from "@base/util/Immutable";
import { Auto } from "@biz/definitions/SunSailDefinitions";
import { CurtainData } from "@biz/items/curtain/CurtainData";
import { CurtainClothAlign, CurtainConfectionType, CurtainSuspensionType, CurtainTieBackType, ICurtainCloth, ICurtainClothAuto, ICurtainData, ICurtainTrack, ICurtainTrackCorner, TrackJointType } from "@biz/items/curtain/CurtainDefinitions";
import { CurtainLogic } from "@biz/items/curtain/CurtainLogic";
import { IClothData } from "@biz/logic/ClothData";
import { getItemLayerReducers } from "@biz/reducers/ItemLayerReducer";
import { getProduct3dReducers } from "@biz/reducers/Product3dReducer";

export const CurtainReducer = {
  ...getItemLayerReducers<ICurtainData>(),
  ...getProduct3dReducers<ICurtainData>(),
  // _addTrack(old: ICurtainData): ICurtainData {
  //   const lastTrack = old.tracks.length > 0 ? old.tracks[old.tracks.length - 1] : undefined;
  //   const newTrack = lastTrack ? CurtainData.createTrack(Vec3.addElements(lastTrack.pos, -1, 0, -1), 2) : CurtainData.createDefaultTrack(Vec3.ZERO);
  //   return {
  //     ...old,
  //     tracks: ImmHelper.pushArrayItem(old.tracks, newTrack)
  //   }
  // },
  removeTrack(old: ICurtainData, trackIndex: number): ICurtainData {
    if (old.tracks.length === 0)
      return old;
    return {
      ...old,
      tracks: ImmHelper.removeArrayItem(old.tracks, trackIndex)
    }
  },
  setTrackPointLength,
  setTrackPointAngle,
  setTrackStartPoint,
  setTrackPoint(old: ICurtainData, trackIndex: number, cornerIndex: number, p: IVec3): ICurtainData {
    const withLength = setTrackPointLength(old, trackIndex, cornerIndex, p);
    if (cornerIndex <= 0)
      return withLength;
    return setTrackPointAngle(withLength, trackIndex, cornerIndex, p);
  },
  addTrackPoint(old: ICurtainData, trackIndex: number, cornerIndex: number | undefined, jointType: TrackJointType, dir: "left" | "right" | "straight"): ICurtainData {
    return updateTrack(old, trackIndex, (track) => CurtainTrackReducer.addTrackPoint(track, cornerIndex, jointType, dir));
  },
  removeTrackPoint(old: ICurtainData, trackIndex: number, cornerIndex: number): ICurtainData {
    return updateTrack(old, trackIndex, (track) => CurtainTrackReducer.removeTrackPoint(track, cornerIndex));
  },
  setTopY(old: ICurtainData, topY: number): ICurtainData {
    return {
      ...old,
      pos: {
        ...old.pos,
        y: topY
      }
    }
  },
  setPos(old: ICurtainData, pos: IVec3): ICurtainData {
    return {
      ...old,
      pos
    }
  },
  setTransform(old: ICurtainData, pos: IVec3, rotationY: number): ICurtainData {
    return {
      ...old,
      pos,
      rotationY
    }
  },

  addCloth(old: ICurtainData, trackIndex: number, options: { anchorPoint?: number, align?: CurtainClothAlign }): ICurtainData {
    const newCloth = CurtainData.createCloth();
    if (options?.anchorPoint !== undefined)
      newCloth.anchorPoint = options.anchorPoint;
    if (options?.align !== undefined)
      newCloth.align = options.align;
    return updateTrack(old, trackIndex, (track) => CurtainTrackReducer.addCloth(track, newCloth))
  },
  removeCloth(old: ICurtainData, trackIndex: number, clothIndex: number): ICurtainData {
    return updateTrack(old, trackIndex, (track) => CurtainTrackReducer.removeCloth(track, clothIndex))
  },
  setClothWidth(old: ICurtainData, width: number): ICurtainData {
    return ImmHelper.setProperty(old, "clothGlobal", "width", width);
  },
  setClothHeight(old: ICurtainData, height: number): ICurtainData {
    return ImmHelper.setProperty(old, "clothGlobal", "height", height);
  },
  setClothAdditionFactor,
  setClothConfectionType(old: ICurtainData, clothMountType: CurtainConfectionType): ICurtainData {
    return ImmHelper.setProperty(old, "clothGlobal", "confectionType", clothMountType);
  },
  setClothSuspensionType(old: ICurtainData, suspensionType: CurtainSuspensionType): ICurtainData {
    return ImmHelper.setProperty(old, "clothGlobal", "suspensionType", suspensionType);
  },
  setClothTieBackType(old: ICurtainData, setClothTieBackType: CurtainTieBackType | undefined): ICurtainData {
    return ImmHelper.setProperty(old, "clothGlobal", "tieBackType", setClothTieBackType);
  },
  setClothData(old: ICurtainData, newData: { cloth?: IClothData, colorId?: string, additionFactor?: number }, descriptor?: { trackIndex: number, clothIndex: number }): ICurtainData {
    if (!descriptor) {
      return ImmHelper.setProperty(old, "clothGlobal", CurtainClothReducer.setClothData(old.clothGlobal, newData));
    } else {
      return updateCloth(old, descriptor, (clothData) => CurtainClothReducer.setClothData(clothData, newData))
    }
  },
  setMaxFixDist(old: ICurtainData, maxFixDist: number): ICurtainData {
    return ImmHelper.setProperty(old, "clothGlobal", "maxFixDist", maxFixDist)
  }
}

function setTrackPointLength(old: ICurtainData, trackIndex: number, cornerIndex: number, p: IVec3): ICurtainData {
  return updateTrack(old, trackIndex, (track) => {
    if (cornerIndex > 0) {
      const prevCorner = CurtainLogic.getCornerPosition(old, trackIndex, cornerIndex - 1);
      let length = Vec3.len(prevCorner, p);
      const maxLength = CurtainLogic.getMaxTrackPartLength(old.curtainType);
      if (maxLength !== undefined)
        length = Math.min(length, maxLength);
      return {
        ...track,
        corners: ImmHelper.setArrayItem(track.corners, cornerIndex, {
          ...track.corners[cornerIndex],
          length,
        })
      }
    }
    return {
      ...track,
      pos: Vec3.withY(p, track.pos.y)
    }
  });
}

function setTrackStartPoint(old: ICurtainData, trackIndex: number, p: IVec3): ICurtainData {
  return updateTrack(old, trackIndex, (track) => {
    return {
      ...track,
      pos: Vec3.withY(p, track.pos.y)
    }
  });
}

function setTrackPointAngle(old: ICurtainData, trackIndex: number, cornerIndex: number, p: IVec3): ICurtainData {
  if (cornerIndex <= 0)
    return old;
  return updateTrack(old, trackIndex, (track) => {
    const pcOld = Vec3.dir(CurtainLogic.getCornerPosition(old, trackIndex, 0), CurtainLogic.getCornerPosition(old, trackIndex, cornerIndex));
    const pcNew = Vec3.dir(CurtainLogic.getCornerPosition(old, trackIndex, 0), p);
    const angleOld = Math.atan2(-pcOld.z, pcOld.x)
    const angleNew = Math.atan2(-pcNew.z, pcNew.x)
    return {
      ...track,
      angle: track.angle + angleNew - angleOld
    }
  });
}

function setClothAdditionFactor(old: ICurtainData, additionFactor: Auto<number>): ICurtainData;
function setClothAdditionFactor(old: ICurtainData, additionFactor: Auto<number> | undefined, descriptor?: { trackIndex: number, clothIndex: number }): ICurtainData;
function setClothAdditionFactor(old: ICurtainData, additionFactor: Auto<number> | undefined, descriptor?: { trackIndex: number, clothIndex: number }): ICurtainData {
  if (!descriptor) {
    return ImmHelper.setProperty(old, "clothGlobal", "additionFactor", additionFactor!);
  } else
    return updateCloth(old, descriptor, (cloth) => CurtainClothReducer.setClothAdditionFactor(cloth, additionFactor))
}

function updateTrack(old: ICurtainData, trackIndex: number, newTrack: ICurtainTrack | ((track: ICurtainTrack) => ICurtainTrack)): ICurtainData {
  return {
    ...old,
    tracks: ImmHelper.setArrayItem(old.tracks, trackIndex, typeof newTrack === "function" ? newTrack(old.tracks[trackIndex]) : newTrack)
  }
}

function updateCloth(old: ICurtainData, descriptor: { trackIndex: number, clothIndex: number }, newCloth: ICurtainCloth | ((cloth: ICurtainCloth) => ICurtainCloth)): ICurtainData {
  return updateTrack(old, descriptor.trackIndex, (track) => ({
    ...track,
    clothes: ImmHelper.setArrayItem(track.clothes, descriptor.clothIndex, typeof newCloth === "function" ? newCloth(track.clothes[descriptor.clothIndex]) : newCloth)
  }));
}

export const CurtainTrackReducer = {
  addTrackPoint(old: ICurtainTrack, cornerIndex: number | undefined, jointType: TrackJointType, dir: "left" | "right" | "straight"): ICurtainTrack {
    const corners = old.corners;
    if (cornerIndex === undefined)
      cornerIndex = corners.length - 1;

    let oldPoint = old.corners[cornerIndex];
    const angle = { "left": Math.PI * 0.5, "right": -Math.PI * 0.5, "straight": 0 }[dir];

    if (cornerIndex === old.corners.length - 1) {
      const newCorner: ICurtainTrackCorner = {
        jointType: "stop",
        angle: 0,
        length: 1,
      }

      oldPoint = ImmHelper.setProperty(oldPoint, "angle", angle);
      oldPoint = ImmHelper.setProperty(oldPoint, "jointType", jointType);
      return {
        ...old,
        corners: ImmHelper.pushArrayItem(ImmHelper.setArrayItem(corners, cornerIndex, oldPoint), newCorner)
      }
    } else {
      const newCorner: ICurtainTrackCorner = {
        jointType,
        angle,
        length: 1,
      }

      return {
        ...old,
        corners: ImmHelper.insertArrayItem(corners, cornerIndex + 1, newCorner)
      }
    }
  },
  // setTrackPoint(old: ICurtainTrack, cornerIndex: number, p: IVec3): ICurtainTrack {
  //   return {
  //     ...old,
  //     corners: ImmHelper.setArrayItem(old.corners, cornerIndex, { ...old.corners[cornerIndex], pos: p })
  //   }
  // },
  removeTrackPoint(old: ICurtainTrack, cornerIndex: number): ICurtainTrack {
    return {
      ...old,
      corners: ImmHelper.removeArrayItem(old.corners, cornerIndex)
    }
  },
  addCloth(old: ICurtainTrack, newCloth: ICurtainCloth): ICurtainTrack {
    return {
      ...old,
      clothes: ImmHelper.pushArrayItem(old.clothes, newCloth),
    }
  },
  removeCloth(old: ICurtainTrack, clothIndex: number): ICurtainTrack {
    return {
      ...old,
      clothes: ImmHelper.removeArrayItem(old.clothes, clothIndex)
    }
  },
  setTrackSegmentLength(old: ICurtainTrack, cornerIndex: number, length: number): ICurtainTrack {
    const newCorners = ImmHelper.setArrayItem(old.corners, cornerIndex, CurtainTrackCornerReducer.setLength(old.corners[cornerIndex], length))
    return ImmHelper.setProperty(old, "corners", newCorners);
  },
  setTrackSegmentAngle(old: ICurtainTrack, cornerIndex: number, angle: number): ICurtainTrack {
    if (cornerIndex === 0)
      return ImmHelper.setProperty(old, "angle", angle)
    const newCorners = ImmHelper.setArrayItem(old.corners, cornerIndex, CurtainTrackCornerReducer.setAngle(old.corners[cornerIndex], angle))
    return ImmHelper.setProperty(old, "corners", newCorners);
  },
}

export const CurtainTrackCornerReducer = {
  setAngle(old: ICurtainTrackCorner, angle: number): ICurtainTrackCorner {
    return ImmHelper.setProperty(old, "angle", angle);
  },
  setLength(old: ICurtainTrackCorner, angle: number): ICurtainTrackCorner {
    return ImmHelper.setProperty(old, "length", angle);
  },
  setJointType(old: ICurtainTrackCorner, jointType: TrackJointType): ICurtainTrackCorner {
    old = ImmHelper.setProperty(old, "jointType", jointType);
    if (jointType === "arc")
      old = ImmHelper.setProperty(old, "angle", old.angle >= 0 ? Math.PI * 0.5 : -Math.PI * 0.5)
    return old;
  },
  setArticleSlot(old: ICurtainTrackCorner, name: keyof NonNullable<ICurtainTrackCorner["articleSlots"]>, articleId: string | undefined): ICurtainTrackCorner {
    let articleSlots = old.articleSlots || {};
    articleSlots = ImmHelper.setProperty(articleSlots, name, articleId);
    return ImmHelper.setProperty(old, "articleSlots", articleSlots)
  },
}

export const CurtainClothReducer = {
  setClothHeight(old: ICurtainCloth, height: number | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "height", height);
  },
  setClothWidth(old: ICurtainCloth, width: number | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "width", width);
  },
  setClothOffset(old: ICurtainCloth, offset: number | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "offset", offset);
  },
  setClothAlign(old: ICurtainCloth, align: CurtainClothAlign | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "align", align);
  },
  setClothAnchorPoint(old: ICurtainCloth, anchorPoint: number | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "anchorPoint", anchorPoint);
  },
  setClothAdditionFactor(old: ICurtainCloth, additionFactor: Auto<number> | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "additionFactor", additionFactor);
  },
  setClothData<T extends Partial<ICurtainClothAuto>>(old: T, newData: { cloth?: IClothData, colorId?: string, additionFactor?: number }): T {
    return {
      ...old,
      ...newData
    }
  },
  setClothConfectionType(old: ICurtainCloth, confectionType: CurtainConfectionType | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "confectionType", confectionType);
  },
  setClothSuspensionType(old: ICurtainCloth, suspensionType: CurtainSuspensionType | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "suspensionType", suspensionType);
  },
  setClothTieBackType(old: ICurtainCloth, tieBackType: CurtainTieBackType | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "tieBackType", tieBackType);
  },
  setClothTieBackLength(old: ICurtainCloth, tieBackLength: Auto<number>): ICurtainCloth {
    return ImmHelper.setProperty(old, "tieBackLength", tieBackLength);
  },
  setMaxFixDist(old: ICurtainCloth, maxFixDist: number | undefined): ICurtainCloth {
    return ImmHelper.setProperty(old, "maxFixDist", maxFixDist);
  }
}